import React, { useState } from 'react';
import { X, ShieldCheck, FileText, Cookie, Lock, CheckCircle2 } from 'lucide-react';
import { CkLogo } from './CkLogo';

export type LegalDocType = 'privacy' | 'terms' | 'cookies';

interface LegalModalsProps {
  isOpen: boolean;
  onClose: () => void;
  initialDoc?: LegalDocType;
}

export const LegalModals: React.FC<LegalModalsProps> = ({
  isOpen,
  onClose,
  initialDoc = 'privacy'
}) => {
  const [activeDoc, setActiveDoc] = useState<LegalDocType>(initialDoc);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-3xl max-h-[90vh] flex flex-col bg-[#0f111a]/95 border border-cyan-500/30 rounded-2xl shadow-[0_0_50px_rgba(0,240,255,0.15)] overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <CkLogo size={32} />
            <div>
              <h2 className="text-lg font-bold text-white tracking-wide">Legal &amp; Compliance Center</h2>
              <p className="text-xs text-gray-400">Zero-Knowledge Architecture &amp; Privacy Transparency</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-white/5 bg-black/20 overflow-x-auto">
          {[
            { id: 'privacy', label: 'Privacy Policy', icon: ShieldCheck },
            { id: 'terms', label: 'Terms of Service', icon: FileText },
            { id: 'cookies', label: 'Cookie & Storage Policy', icon: Cookie },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeDoc === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveDoc(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-xl transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_15px_rgba(0,240,255,0.15)]'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5 border border-transparent'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar text-gray-300 text-xs leading-relaxed">
          
          {/* PRIVACY POLICY */}
          {activeDoc === 'privacy' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <ShieldCheck size={18} className="text-cyan-400" />
                  CK Share Privacy Policy
                </h3>
                <span className="text-[10px] text-gray-400">Last updated: August 2026</span>
              </div>

              <div className="p-3.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-200">
                <strong>Core Privacy Principle:</strong> CK Share is built on a <strong>Zero-Knowledge Cryptographic Architecture</strong>. We do not know who you are, what you copy, or what devices you sync. Your data is encrypted locally on your device with AES-GCM-256 before being routed.
              </div>

              <h4 className="text-sm font-semibold text-white pt-2">1. Information We Process</h4>
              <p>
                CK Share routes real-time clipboard snippets, text files, and quick drop items between your authorized devices. All content is encrypted client-side using cryptographic keys derived from your Room Access Code.
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-400">
                <li><strong>Plaintext Clipboard:</strong> Stays exclusively on your devices in RAM. Plaintext NEVER touches any server.</li>
                <li><strong>Encrypted Ciphertext:</strong> Transmitted via WebSocket over Cloudflare Edge Workers &amp; Durable Objects. Discarded immediately after relay.</li>
                <li><strong>No User Accounts:</strong> No email, telephone, login, or personal identifiers are collected or stored.</li>
              </ul>

              <h4 className="text-sm font-semibold text-white pt-2">2. Android Accessibility Service API Disclosure</h4>
              <p>
                In compliance with Google Play Developer Policies and Android OS Security guidelines, we disclose the exact usage of the Android Accessibility Service:
              </p>
              <div className="p-3 rounded-lg bg-black/40 border border-white/5 space-y-1.5 text-gray-400">
                <p>• <strong>Purpose:</strong> To observe when you tap "Copy" in third-party applications (such as Chrome, WhatsApp, and Notes) to trigger automatic background synchronization to your PC.</p>
                <p>• <strong>Scope:</strong> The service does NOT log keystrokes, does NOT inspect password fields, and does NOT share data with third parties or advertisers.</p>
                <p>• <strong>Control:</strong> You can disable this service at any time in Android Settings ➔ Accessibility.</p>
              </div>

              <h4 className="text-sm font-semibold text-white pt-2">3. Third-Party Infrastructure</h4>
              <p>
                CK Share utilizes <strong>Cloudflare Workers and Durable Objects</strong> for real-time WebSocket routing. Cloudflare operates as a blind relay and does not retain persistent logs of WebSocket packet payloads.
              </p>

              <h4 className="text-sm font-semibold text-white pt-2">4. Your Data Rights (GDPR / CCPA)</h4>
              <p>
                Because CK Share stores no personal data or server-side logs, there is no personal profile to delete. Clearing your browser cache or uninstalling the app permanently erases all local data.
              </p>
            </div>
          )}

          {/* TERMS OF SERVICE */}
          {activeDoc === 'terms' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <FileText size={18} className="text-purple-400" />
                  Terms of Service &amp; Fair Use
                </h3>
                <span className="text-[10px] text-gray-400">Version 1.2.0</span>
              </div>

              <h4 className="text-sm font-semibold text-white">1. Agreement to Terms</h4>
              <p>
                By accessing or using the CK Share web application, Android application, or Windows desktop companion, you agree to be bound by these Terms of Service. If you do not agree, please do not use the software.
              </p>

              <h4 className="text-sm font-semibold text-white pt-2">2. Open Source &amp; Software License</h4>
              <p>
                CK Share is provided as open-source software under standard community terms. You are granted a free, non-exclusive license to use the software for personal, academic, and commercial purposes.
              </p>

              <h4 className="text-sm font-semibold text-white pt-2">3. User Responsibility &amp; Encryption Keys</h4>
              <p>
                You are solely responsible for maintaining the confidentiality of your Room Code and Password. Because CK Share uses zero-knowledge encryption, lost passwords cannot be recovered by server administrators.
              </p>

              <h4 className="text-sm font-semibold text-white pt-2">4. Acceptable Use</h4>
              <p>
                You agree not to use CK Share to transmit malicious software, engage in denial-of-service attacks against the relay infrastructure, or violate applicable local or international laws.
              </p>

              <h4 className="text-sm font-semibold text-white pt-2">5. Disclaimer of Warranties</h4>
              <p>
                The software is provided "AS IS", without warranty of any kind, express or implied. In no event shall the developers be liable for any claim, damages, or data loss arising from the use of the software.
              </p>
            </div>
          )}

          {/* COOKIE & STORAGE POLICY */}
          {activeDoc === 'cookies' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Cookie size={18} className="text-amber-400" />
                  Cookie &amp; Local Storage Policy
                </h3>
                <span className="text-[10px] text-gray-400">Strict Privacy</span>
              </div>

              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-200">
                <strong>Zero Tracking Cookies:</strong> CK Share does not use advertising cookies, Google Analytics, telemetry trackers, or fingerprinting scripts.
              </div>

              <h4 className="text-sm font-semibold text-white">How We Use Browser Local Storage</h4>
              <p>
                We use browser <code className="text-cyan-300">localStorage</code> strictly for functional preferences required to operate the application:
              </p>

              <div className="space-y-2">
                {[
                  { key: 'ck_room_id', desc: 'Saves your active Room ID so you do not have to type it every session.' },
                  { key: 'ck_device_id', desc: 'Unique random UUID for peer presence in room.' },
                  { key: 'ck_settings', desc: 'Saves UI preferences (sound effects, auto-copy toggle, theme).' },
                  { key: 'ck_cookie_consent', desc: 'Remembers that you acknowledged this privacy consent banner.' }
                ].map((item, i) => (
                  <div key={i} className="p-3 rounded-lg bg-black/40 border border-white/5 flex items-start gap-2.5">
                    <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <code className="text-cyan-300 font-bold">{item.key}</code>
                      <p className="text-gray-400 text-[11px] mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h4 className="text-sm font-semibold text-white pt-2">Managing Your Storage</h4>
              <p>
                You can delete all stored preferences at any time by clicking "Disconnect / Clear Data" inside the Settings modal or clearing site cookies in your browser settings.
              </p>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-white/[0.02] flex items-center justify-between">
          <p className="text-xs text-gray-500">CK Share • Zero Knowledge Protocol</p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
};
